import { useCallback, useState } from 'react';

import { ModalAuthScreen, ModalAuthState } from '../types';

export interface ModalAuthHook {
  modalAuthState: ModalAuthState;
  setModalAuthState: (modalAuthstate: ModalAuthState) => void;
  modalAuthIsOpen: boolean;
}

// This hook is used to track the modal auth state. Which is used to determine if the user is in the modal auth state or not.
export const useModalAuth = (): ModalAuthHook => {
  const [modalAuthState, setModalAuthState] = useState<ModalAuthState>({
    screen: ModalAuthScreen.CLOSED,
  });

  const modalAuthIsOpen = modalAuthState.screen !== ModalAuthScreen.CLOSED;

  /**
   * A handler function to update the modal authentication state.
   * It merges the new state with the previous state, ensuring that no data is lost
   * and only specified properties are updated.
   *
   * @param {Partial<ModalAuthState>} newState - The new state to be merged with the previous state.
   */
  const handleSetModalAuthState = useCallback(
    ({ screen, errorMessage, user, loginForVendorIsLoading }: Partial<ModalAuthState>) => {
      setModalAuthState(prevModalAuthState => ({
        screen: screen ?? prevModalAuthState.screen,
        errorMessage: errorMessage ?? prevModalAuthState.errorMessage,
        user: user
          ? {
              ...prevModalAuthState.user,
              name: user.name ?? prevModalAuthState.user?.name,
              email: user.email ?? prevModalAuthState.user?.email,
            }
          : prevModalAuthState.user,
        loginForVendorIsLoading:
          loginForVendorIsLoading ?? prevModalAuthState.loginForVendorIsLoading,
      }));
    },
    []
  );

  return { modalAuthState, setModalAuthState: handleSetModalAuthState, modalAuthIsOpen };
};
